import React, { useState } from 'react';
import Container from './ui/Container';
import { Button } from './ui/Button';
import PageSEO from './common/PageSEO';
import { useT } from '../i18n';

const T = {
  es: {
    seo: {
      title: 'Contacto - Cadenia',
      description: 'Contacte con Cadenia para conocer nuestra plataforma de visibilidad, inteligencia y documentos de transporte digitales. Le respondemos en menos de 24 horas.',
      keywords: 'contacto cadenia, demo cadenia, solicitar demo logística, plataforma logística',
    },
    notifications: {
      rateLimit: 'Por favor, espere unos segundos antes de enviar otro mensaje.',
      minTime: 'Por favor, tómese un momento para revisar el formulario.',
      tooManyAttempts: 'Demasiados intentos. Por favor, recargue la página.',
      success: '¡Gracias por contactarnos! Le responderemos pronto.',
      error: 'Error al enviar el mensaje. Por favor, inténtelo de nuevo.',
    },
    heading: 'Contacto',
    subheading: '¿Tiene alguna pregunta? Nos encantaría saber de usted.',
    info: {
      emailLabel: 'Email',
      scheduleLabel: 'Horario',
      scheduleValue: 'Lunes - Viernes: 9:00 - 18:00',
    },
    form: {
      heading: 'Envíanos un mensaje',
      nameLabel: 'Nombre *',
      namePlaceholder: 'Su nombre',
      emailLabel: 'Email *',
      emailPlaceholder: 'su@email.com',
      companyLabel: 'Empresa *',
      companyPlaceholder: 'Tu empresa',
      messageLabel: 'Mensaje *',
      messagePlaceholder: '¿En qué podemos ayudarle?',
      consent: 'Acepto el tratamiento de mis datos para responder a mi consulta',
      submitting: 'Enviando...',
      submit: 'Enviar mensaje',
    },
  },
  en: {
    seo: {
      title: 'Contact - Cadenia',
      description: 'Get in touch with Cadenia to learn about our platform for visibility, intelligence and digital transport documents. We reply within 24 hours.',
      keywords: 'contact cadenia, cadenia demo, request logistics demo, logistics platform',
    },
    notifications: {
      rateLimit: 'Please wait a few seconds before sending another message.',
      minTime: 'Please take a moment to review the form.',
      tooManyAttempts: 'Too many attempts. Please reload the page.',
      success: 'Thank you for reaching out! We will get back to you shortly.',
      error: 'There was an error sending your message. Please try again.',
    },
    heading: 'Contact',
    subheading: 'Have a question? We would love to hear from you.',
    info: {
      emailLabel: 'Email',
      scheduleLabel: 'Business hours',
      scheduleValue: 'Monday - Friday: 9:00 - 18:00',
    },
    form: {
      heading: 'Send us a message',
      nameLabel: 'Name *',
      namePlaceholder: 'Your name',
      emailLabel: 'Email *',
      emailPlaceholder: 'you@email.com',
      companyLabel: 'Company *',
      companyPlaceholder: 'Your company',
      messageLabel: 'Message *',
      messagePlaceholder: 'How can we help you?',
      consent: 'I accept the processing of my data for the purpose of responding to my enquiry',
      submitting: 'Sending...',
      submit: 'Send message',
    },
  },
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    consent: false,
    honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [timestamp] = useState(Date.now());
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [lastSubmit, setLastSubmit] = useState(0);
  const t = useT(T);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Protecciones básicas
    const now = Date.now();
    const timeSinceLoad = now - timestamp;
    const timeSinceLastSubmit = now - lastSubmit;

    // Honeypot check
    if (formData.honeypot) {
      return; // Bot detectado
    }

    // Rate limiting
    if (timeSinceLastSubmit < 5000) {
      setNotification({ type: 'warning', message: t.notifications.rateLimit });
      return;
    }

    // Tiempo mínimo en página
    if (timeSinceLoad < 3000) {
      setNotification({ type: 'warning', message: t.notifications.minTime });
      return;
    }

    // Límite de intentos
    if (submitAttempts >= 3) {
      setNotification({ type: 'error', message: t.notifications.tooManyAttempts });
      return;
    }

    setIsSubmitting(true);
    setSubmitAttempts(prev => prev + 1);
    setLastSubmit(now);

    try {
      const response = await fetch('https://a8e28svmy6.execute-api.eu-west-3.amazonaws.com/prod/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'ELZglGzAcUaeexE0s2MmPhaWnRlOTN6H5YU8GY10'
        },
        body: JSON.stringify({
          ...formData,
          timestamp,
          timeSinceLoad,
          source: 'contact-page'
        })
      });

      if (response.ok) {
        setNotification({ type: 'success', message: t.notifications.success });
        setFormData({ name: '', email: '', company: '', message: '', consent: false, honeypot: '' });
      } else {
        throw new Error('Error al enviar');
      }
    } catch (error) {
      setNotification({ type: 'error', message: t.notifications.error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-20 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <PageSEO
        title={t.seo.title}
        description={t.seo.description}
        keywords={t.seo.keywords}
      />
      <Container>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-light text-slate-900 mb-6">
              {t.heading}
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t.subheading}
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Info de contacto — horizontal */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-16">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">{t.info.emailLabel}</div>
                  <div className="text-sm text-slate-700 font-medium">info@cadenia.eu</div>
                </div>
              </div>
              <div className="hidden sm:block w-px h-8 bg-slate-200"></div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">{t.info.scheduleLabel}</div>
                  <div className="text-sm text-slate-700 font-medium">{t.info.scheduleValue}</div>
                </div>
              </div>
            </div>

            {/* Formulario — ancho completo */}
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-slate-100">
              <h2 className="text-2xl font-light text-slate-900 mb-8">{t.form.heading}</h2>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      {t.form.nameLabel}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t.form.namePlaceholder}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      {t.form.emailLabel}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t.form.emailPlaceholder}
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                    {t.form.companyLabel}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t.form.companyPlaceholder}
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    {t.form.messageLabel}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder={t.form.messagePlaceholder}
                  ></textarea>
                </div>

                <div className="mb-6">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleChange}
                      required
                      className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                    />
                    <span className="text-sm text-slate-600">
                      {t.form.consent}
                    </span>
                  </label>
                </div>

                {/* Honeypot field */}
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  style={{position: 'absolute', left: '-9999px', opacity: 0}}
                  tabIndex="-1"
                  autoComplete="off"
                />

                {notification && (
                  <div className={`p-4 rounded-lg mb-6 ${
                    notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                    notification.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                    'bg-yellow-50 text-yellow-800 border border-yellow-200'
                  }`}>
                    <div className="flex justify-between items-start">
                      <p className="text-sm">{notification.message}</p>
                      <button
                        onClick={() => setNotification(null)}
                        className="ml-4 text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full md:w-auto md:px-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  {isSubmitting ? t.form.submitting : t.form.submit}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ContactPage;
